import { useStores } from '@littlebird/sdk';
import { usePagedLoad } from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import {
  forwardRef,
  useCallback,
  useEffect,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui';
import {
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from '@/components/ui/context-menu';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { EntityIcon } from '@/components/ui/icon-picker';
import { SCROLL_LOAD_MORE_THRESHOLD } from '@/lib/scroll-load-more';
import { cn } from '@/lib/utils';

import { CreateProjectModal } from './create-project-modal';
import {
  type ProjectAssignmentTarget,
  useProjectActions,
} from './project-actions';
import {
  type ProjectMenuOption,
  toProjectMenuOption,
} from './project-menu-option';

type MoveToProjectProps = { threadId: string } | { meetingId: string };

export const PROJECT_LABELS = {
  add: 'Add to project',
  move: 'Move to project',
  create: 'Create project',
  none: 'No project',
} as const;

function ProjectMenuItemContent({
  name,
  isSelected,
}: Pick<ProjectMenuOption, 'name' | 'isSelected'>) {
  return (
    <>
      <span className="flex-1 truncate">{name}</span>
      {isSelected && (
        <Icons.check className="ml-auto size-3.5 shrink-0 opacity-75" />
      )}
    </>
  );
}

function getProjectTarget(props: MoveToProjectProps): ProjectAssignmentTarget {
  return 'threadId' in props
    ? { kind: 'thread', id: props.threadId }
    : { kind: 'meeting', id: props.meetingId };
}

export function useProjectsMenuScrollLoadMore() {
  const { projects } = useStores();
  const { runLoadMore: runLoadMoreProjects } = usePagedLoad({
    enabled: true,
    canLoadMore: projects.hasMorePages,
    loadPage: () => projects.loadNextPage(),
  });

  return useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const nearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight <
        SCROLL_LOAD_MORE_THRESHOLD;
      if (nearBottom) void runLoadMoreProjects();
    },
    [runLoadMoreProjects]
  );
}

export function useMoveToProject(target: ProjectAssignmentTarget) {
  const { projects } = useStores();
  const { setProjectAssignment } = useProjectActions();

  const currentProject =
    target.kind === 'thread'
      ? projects.getProjectForThread(target.id)
      : projects.getProjectForMeeting(target.id);

  const setProject = (projectId: string | null) => {
    void setProjectAssignment(target, projectId);
  };

  const handleSelectProject = (projectId: string) => {
    setProject(currentProject?.id === projectId ? null : projectId);
  };

  const projectOptions: ProjectMenuOption[] = projects.orderedData.map(
    (project) => toProjectMenuOption(project, currentProject?.id === project.id)
  );

  const showCreateProjectModal = () => {
    ModalsManager.show(CreateProjectModal, {
      navigateOnCreate: false,
      waitForServer: true,
      onCreate: async (project) => {
        await setProjectAssignment(target, project.id);
      },
    });
  };

  const clearProject = () => setProject(null);

  return {
    currentProject,
    projectOptions,
    handleSelectProject,
    clearProject,
    showCreateProjectModal,
  };
}

export const MoveToProjectSubMenu = observer(
  function MoveToProjectSubMenu(props: { threadId: string }) {
    const target = getProjectTarget(props);
    const {
      currentProject,
      projectOptions,
      handleSelectProject,
      clearProject,
      showCreateProjectModal,
    } = useMoveToProject(target);
    const onProjectsMenuScroll = useProjectsMenuScrollLoadMore();

    return (
      <ContextMenuSub>
        <ContextMenuSubTrigger>
          <Icons.folder className="size-3.5 opacity-75" />
          {currentProject ? PROJECT_LABELS.move : PROJECT_LABELS.add}
        </ContextMenuSubTrigger>
        <ContextMenuSubContent
          className="max-h-64 overflow-y-auto"
          onScroll={onProjectsMenuScroll}
        >
          {currentProject && (
            <ContextMenuItem icon={Icons.circleDashed} onClick={clearProject}>
              {PROJECT_LABELS.none}
            </ContextMenuItem>
          )}
          {projectOptions.map((project) => (
            <ContextMenuItem
              key={project.id}
              onClick={() => handleSelectProject(project.id)}
            >
              <EntityIcon icon={project.icon} muted />
              <ProjectMenuItemContent
                name={project.name}
                isSelected={project.isSelected}
              />
            </ContextMenuItem>
          ))}
          {projectOptions.length > 0 && <ContextMenuSeparator />}
          <ContextMenuItem
            icon={Icons.folderPlus}
            onClick={showCreateProjectModal}
          >
            {PROJECT_LABELS.create}
          </ContextMenuItem>
        </ContextMenuSubContent>
      </ContextMenuSub>
    );
  }
);

export const MoveToProjectDropdownSubMenu = observer(
  function MoveToProjectDropdownSubMenu(props: MoveToProjectProps) {
    const target = getProjectTarget(props);
    const {
      currentProject,
      projectOptions,
      handleSelectProject,
      clearProject,
      showCreateProjectModal,
    } = useMoveToProject(target);
    const onProjectsMenuScroll = useProjectsMenuScrollLoadMore();

    return (
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger>
          <Icons.folder className="size-3.5 opacity-75" />
          {currentProject ? PROJECT_LABELS.move : PROJECT_LABELS.add}
        </DropdownMenu.SubTrigger>
        <DropdownMenu.SubContent onScroll={onProjectsMenuScroll}>
          <ProjectDropdownItems
            projectOptions={projectOptions}
            onCreateProject={showCreateProjectModal}
            onSelectProject={handleSelectProject}
            onClearProject={clearProject}
          />
        </DropdownMenu.SubContent>
      </DropdownMenu.Sub>
    );
  }
);

export interface ProjectDropdownItemsProps {
  projectOptions: ProjectMenuOption[];
  onCreateProject: () => void;
  onSelectProject: (projectId: string) => void;
  onClearProject?: () => void;
}

export function ProjectDropdownItems({
  projectOptions,
  onCreateProject,
  onSelectProject,
  onClearProject,
}: ProjectDropdownItemsProps) {
  const hasSelectedProject = projectOptions.some((p) => p.isSelected);

  return (
    <>
      {hasSelectedProject && onClearProject && (
        <DropdownMenu.Item onClick={onClearProject}>
          <Icons.circleDashed className="size-3.5 opacity-75" />
          {PROJECT_LABELS.none}
        </DropdownMenu.Item>
      )}
      {projectOptions.map((project) => (
        <DropdownMenu.Item
          key={project.id}
          onClick={() => onSelectProject(project.id)}
        >
          <EntityIcon icon={project.icon} muted />
          <ProjectMenuItemContent
            name={project.name}
            isSelected={project.isSelected}
          />
        </DropdownMenu.Item>
      ))}
      {projectOptions.length > 0 && <DropdownMenu.Separator />}
      <DropdownMenu.Item onClick={onCreateProject}>
        <Icons.folderPlus className="size-3.5 opacity-75" />
        {PROJECT_LABELS.create}
      </DropdownMenu.Item>
    </>
  );
}

export interface MoveToProjectTriggerProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  label: string;
  icon?: ReactNode;
  isCompactAssigned?: boolean;
}

export const MoveToProjectTrigger = forwardRef<
  HTMLButtonElement,
  MoveToProjectTriggerProps
>(function MoveToProjectTrigger(
  {
    label,
    icon = <Icons.folderPlus className="size-3.5 shrink-0 opacity-75" />,
    isCompactAssigned = false,
    className,
    disabled,
    onClick,
    onKeyDown,
    ...props
  },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      className={cn(
        'inline-flex h-8 max-w-full items-center gap-1.5 rounded-lg px-2 text-sm',
        'text-foreground-muted hover:text-foreground hover:bg-secondary-hover',
        'data-[state=open]:text-foreground data-[state=open]:bg-secondary-active',
        'transition-colors focus-ring',
        className
      )}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
      onKeyDown={(event) => {
        event.stopPropagation();
        onKeyDown?.(event);
      }}
      {...props}
    >
      {icon}
      <span
        className={cn(
          'truncate',
          isCompactAssigned && 'hidden group-hover:inline'
        )}
      >
        {label}
      </span>
      <Icons.chevronDown
        className={cn(
          'h-3 w-3 shrink-0 opacity-70',
          isCompactAssigned && 'hidden group-hover:block'
        )}
      />
    </button>
  );
});

export const MoveToProjectDropdownButton = observer(
  function MoveToProjectDropdownButton({
    className,
    meetingId,
    open,
    onOpenChange,
  }: {
    className?: string;
    meetingId: string;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
  }) {
    const target = getProjectTarget({ meetingId });
    const {
      currentProject,
      projectOptions,
      handleSelectProject,
      clearProject,
      showCreateProjectModal,
    } = useMoveToProject(target);
    const label = currentProject?.name ?? PROJECT_LABELS.add;
    const isCompactAssigned = Boolean(currentProject);
    const triggerIcon = currentProject ? (
      <EntityIcon icon={currentProject.icon} muted />
    ) : undefined;
    const onProjectsMenuScroll = useProjectsMenuScrollLoadMore();

    return (
      <DropdownMenu.Root open={open} onOpenChange={onOpenChange}>
        <DropdownMenu.Trigger asChild>
          <MoveToProjectTrigger
            label={label}
            icon={triggerIcon}
            isCompactAssigned={isCompactAssigned}
            className={className}
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          side="bottom"
          fit
          className="min-w-44 max-w-64"
          onClick={(event) => event.stopPropagation()}
          onScroll={onProjectsMenuScroll}
        >
          <ProjectDropdownItems
            projectOptions={projectOptions}
            onCreateProject={showCreateProjectModal}
            onSelectProject={handleSelectProject}
            onClearProject={clearProject}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
);

export const EnsureMeetingMoveToProjectDropdownButton = observer(
  function EnsureMeetingMoveToProjectDropdownButton({
    className,
    meetingId: meetingIdProp,
    ensureMeetingId,
    onOpenChange,
  }: {
    className?: string;
    meetingId?: string;
    ensureMeetingId: () => Promise<string>;
    onOpenChange?: (open: boolean) => void;
  }) {
    const [resolvedMeetingId, setResolvedMeetingId] = useState<
      string | undefined
    >(meetingIdProp);
    const [open, setOpen] = useState(false);
    const [isEnsuring, setIsEnsuring] = useState(false);

    useEffect(() => {
      if (meetingIdProp) {
        setResolvedMeetingId(meetingIdProp);
      }
    }, [meetingIdProp]);

    const handleOpenChange = useCallback(
      (nextOpen: boolean) => {
        setOpen(nextOpen);
        onOpenChange?.(nextOpen);
      },
      [onOpenChange]
    );

    if (resolvedMeetingId) {
      return (
        <MoveToProjectDropdownButton
          meetingId={resolvedMeetingId}
          open={open}
          onOpenChange={handleOpenChange}
          className={className}
        />
      );
    }

    return (
      <MoveToProjectTrigger
        label={PROJECT_LABELS.add}
        className={className}
        onClick={async () => {
          if (isEnsuring) return;
          setIsEnsuring(true);
          onOpenChange?.(true);
          try {
            const meetingId = await ensureMeetingId();
            setResolvedMeetingId(meetingId);
            setOpen(true);
          } finally {
            setIsEnsuring(false);
          }
        }}
      />
    );
  }
);

export const BulkMoveToProjectDropdown = observer(
  function BulkMoveToProjectDropdown({
    targets,
    itemNoun = 'note',
    onComplete,
    disabled,
  }: {
    targets: ProjectAssignmentTarget[];
    itemNoun?: string;
    onComplete?: () => void;
    disabled?: boolean;
  }) {
    const { projects } = useStores();
    const { setProjectAssignment } = useProjectActions();
    const [isAssigning, setIsAssigning] = useState(false);

    const assignToProject = useCallback(
      async (projectId: string | null) => {
        if (isAssigning) return;
        setIsAssigning(true);
        try {
          const results = await Promise.all(
            targets.map((target) => setProjectAssignment(target, projectId))
          );
          const successCount = results.filter(Boolean).length;
          if (successCount === targets.length) {
            const plural = successCount > 1;
            const project = projectId ? projects.get(projectId) : null;
            toast.success(
              projectId
                ? `Added ${successCount} ${itemNoun}${plural ? 's' : ''} to ${project?.name ?? 'project'}`
                : `Removed ${successCount} ${itemNoun}${plural ? 's' : ''} from ${plural ? 'their projects' : 'its project'}`
            );
          }
          onComplete?.();
        } finally {
          setIsAssigning(false);
        }
      },
      [
        isAssigning,
        targets,
        itemNoun,
        setProjectAssignment,
        projects,
        onComplete,
      ]
    );

    const handleCreateProject = useCallback(() => {
      ModalsManager.show(CreateProjectModal, {
        navigateOnCreate: false,
        waitForServer: true,
        onCreate: async (project) => {
          await assignToProject(project.id);
        },
      });
    }, [assignToProject]);

    const projectOptions: ProjectMenuOption[] = projects.orderedData.map(
      (project) => toProjectMenuOption(project, false)
    );
    const onProjectsMenuScroll = useProjectsMenuScrollLoadMore();

    return (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <Button
            size="sm"
            variant="secondary"
            className="gap-1.5"
            disabled={disabled || isAssigning}
            isLoading={isAssigning}
          >
            <Icons.folder className="size-3.5" />
            {PROJECT_LABELS.add}
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="center"
          side="top"
          fit
          className="min-w-44 max-w-64"
          onScroll={onProjectsMenuScroll}
        >
          <DropdownMenu.Item onClick={() => assignToProject(null)}>
            <Icons.circleDashed className="size-3.5 opacity-75" />
            {PROJECT_LABELS.none}
          </DropdownMenu.Item>
          <ProjectDropdownItems
            projectOptions={projectOptions}
            onCreateProject={handleCreateProject}
            onSelectProject={assignToProject}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
);
